import { useEffect } from 'react';
import { Link } from 'react-router-dom';

import { trackEvent } from '../analytics/posthog';
import { CameraIcon } from '../lib/icons';
import { useUiStore } from '../store/ui-store';

/**
 * "A screenshot is coming in" — the one thing this app cannot show from server
 * state.
 *
 * A capture from the extension is the only write that happens while nobody is
 * looking at it: the user is on their own page, this tab is uploading in the
 * background, and until the row exists every list here truthfully says there is
 * nothing to show. That reads as broken. This says the wait is expected, and
 * then offers the board it landed on.
 *
 * Bottom-centre: bottom-left is the Query devtools button and bottom-right is
 * the capture rail and the canvas zoom controls, both of which are real
 * product UI that a floating pill would eat the clicks of.
 */

/** Long enough to notice and click, short enough not to sit there. */
const DISMISS_AFTER_MS = 8000;

export function CaptureNotice() {
  const incoming = useUiStore((state) => state.incoming);
  const landed = useUiStore((state) => state.landed);
  const failed = useUiStore((state) => state.failed);
  const dismiss = useUiStore((state) => state.dismissCaptureNotice);
  // The local-capture modal narrates the same arrivals, with thumbnails.
  const modalOpen = useUiStore((state) => state.captureModalOpen);

  useEffect(() => {
    if (incoming > 0) return;
    if (!landed && !failed) return;

    const timer = setTimeout(dismiss, DISMISS_AFTER_MS);
    return () => {
      clearTimeout(timer);
    };
  }, [dismiss, failed, incoming, landed]);

  if (modalOpen) return null;
  if (incoming === 0 && !landed && !failed) return null;

  return (
    <div
      aria-live="polite"
      className="pointer-events-none fixed inset-x-0 bottom-6 z-40 flex justify-center px-4"
    >
      <div className="pointer-events-auto flex items-center gap-3 rounded-full border border-hairline bg-surface px-4 py-2.5 shadow-soft duration-200 animate-in fade-in slide-in-from-bottom-2">
        <CameraIcon className="size-4 shrink-0 text-text-muted" />

        {incoming > 0 ? (
          <span className="text-ui text-text-secondary">
            {incoming === 1
              ? 'Adding a screenshot from the extension…'
              : `Adding ${String(incoming)} screenshots from the extension…`}
          </span>
        ) : failed ? (
          <span className="text-ui text-text-secondary">
            A screenshot from the extension could not be saved
          </span>
        ) : (
          <>
            <span className="text-ui text-text-secondary">
              Screenshot added
            </span>
            {landed ? (
              <Link
                className="text-ui font-semibold text-text-primary underline underline-offset-2"
                onClick={() => {
                  trackEvent('capture_notice_opened');
                  dismiss();
                }}
                state={{ from: 'capture_notice' }}
                to={`/sessions/${landed.sessionId}`}
              >
                Open board
              </Link>
            ) : null}
          </>
        )}

        <button
          aria-label="Dismiss"
          className="text-ui text-text-muted hover:text-text-primary"
          onClick={() => {
            trackEvent('capture_notice_dismissed');
            dismiss();
          }}
          type="button"
        >
          ✕
        </button>
      </div>
    </div>
  );
}
